/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useCallback } from 'react';
import StockTable from '../components/StockTable';
import ConsumableWarehouseReportModal from '../components/ConsumableWarehouseReportModal';

// Helper: safe JSON / image string parsing
const defaultParseImages = (imageUrlField) => {
  if (!imageUrlField) return { full: '', zoom: '', other: '', document: '' };
  if (typeof imageUrlField !== 'string') return { full: '', zoom: '', other: '', document: '' };

  if (imageUrlField.startsWith('{') && imageUrlField.endsWith('}')) {
    try {
      const parsed = JSON.parse(imageUrlField);
      return {
        full: parsed.full || '',
        zoom: parsed.zoom || '',
        other: parsed.other || '',
        document: parsed.document || ''
      };
    } catch (e) {
      return { full: imageUrlField, zoom: '', other: '', document: '' };
    }
  }
  return { full: imageUrlField, zoom: '', other: '', document: '' };
};

const StockPage = ({
  currentPage = 'consumable',
  inventorySearchQuery = '',
  setInventorySearchQuery = () => {},
  itemMaster = [],
  supplies = [],
  getStatusConfig = () => ({ label: '', bg: '#ccc' }),
  handleTogglePreview = () => {},
  showPreview = {},
  parseImages = () => ({}),
  setExpandedImage = () => {},
  handleUpdateGlobalMinStock = () => {},
  handleUpdateAmount = () => {},
  handleDeleteMaster = () => {},
  startEdit = () => {},
  onOpenTransfer = () => {},
  onNavigate = () => {}
}) => {
  // UI State
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'low' | 'expiring' | 'normal'
  const [activeWarehouse, setActiveWarehouse] = useState('all'); // 'all' | 'คลังใหญ่' | 'คลังย่อย' | 'ห้องเก็บของ' | 'ห้องคลีนิก'
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportTargetWarehouse, setReportTargetWarehouse] = useState('คลังใหญ่ (คลังพัสดุ)');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [expandedItems, setExpandedItems] = useState({});
  const [localPreview, setLocalPreview] = useState({});

  // Helper to parse images using prop parser or fallback
  const getImages = useCallback((imageUrlField) => {
    if (typeof parseImages === 'function') {
      const res = parseImages(imageUrlField);
      if (res && (res.full || res.zoom || res.other || res.document)) {
        return {
          full: res.full || '',
          zoom: res.zoom || '',
          other: res.other || '',
          document: res.document || ''
        };
      }
    }
    return defaultParseImages(imageUrlField);
  }, [parseImages]);

  // Group master lots by unique item name and calculate stock metrics
  const itemGroups = useMemo(() => {
    // 1. Filter master lots by active page category
    const categoryLots = itemMaster.filter(masterLot => {
      if (currentPage === 'consumable') return masterLot.category === 'วัสดุสิ้นเปลือง' && !masterLot.is_set;
      if (currentPage === 'durable') return (masterLot.category === 'วัสดุคงทน' || masterLot.category === 'ชุดเครื่องมือ');
      return false;
    });

    // 2. Get unique item names
    const uniqueNames = categoryLots.reduce((acc, curr) => {
      if (!acc.includes(curr.item_name)) acc.push(curr.item_name);
      return acc;
    }, []);

    // 3. Compute item details & metrics
    return uniqueNames.map(name => {
      const lots = categoryLots.filter(m => m.item_name === name);
      const firstLot = lots[0] || {};
      const isSetGroup = Boolean(firstLot.is_set);
      const groupGlobalMinStock = firstLot.global_min_stock || 0;

      let usable = 0;
      let totalStock = 0;
      const warnings = [];
      let hasExpired = false;
      let hasExpiringSoon = false;
      let hasLotBelowMin = false;

      lots.forEach(lot => {
        let lotItems = supplies.filter(s => s.master_id === lot.id);
        if (currentPage === 'durable' || lot.is_set) {
          const consumableWarehouses = ["ห้องคลีนิก"];
          lotItems = lotItems.filter(s => !consumableWarehouses.includes(s.warehouse_name));
        }
        const lotTotal = lotItems.reduce((sum, s) => sum + (s.item_amount || 0), 0);
        totalStock += lotTotal;
        const status = getStatusConfig(lotTotal, lot.exp_date);

        if (status.label !== 'หมดอายุ') {
          usable += lotTotal;
        } else {
          hasExpired = true;
        }

        if (status.label === '< 3ด.' || status.label === '3ด.-1ปี') {
          hasExpiringSoon = true;
        }

        if (!isSetGroup && currentPage === 'consumable') {
          if (status.label === 'หมดอายุ') {
            warnings.push(`Lot ${lot.lot_number || '-'} หมดอายุ`);
          } else if (lotTotal < (lot.min_stock || 0)) {
            warnings.push(`Lot ${lot.lot_number || '-'} ต่ำกว่าขั้นต่ำ Lot`);
            hasLotBelowMin = true;
          }
        }
      });

      const isBelowGlobalMin = !isSetGroup && currentPage === 'consumable' && usable < groupGlobalMinStock;
      if (isBelowGlobalMin) {
        warnings.push(`ยอดรวมต่ำกว่าขั้นต่ำรวม`);
      }

      const isLowStock = isBelowGlobalMin || hasLotBelowMin || (usable === 0);
      const isExpiringOrExpired = hasExpired || hasExpiringSoon;

      const sortedExpLots = [...lots].sort((a, b) => new Date(a.exp_date) - new Date(b.exp_date));
      const lotStatusDots = (!isSetGroup && currentPage === 'consumable')
        ? sortedExpLots.map(lot => {
            let lotItems = supplies.filter(s => s.master_id === lot.id);
            if (currentPage === 'durable' || lot.is_set) {
              const consumableWarehouses = ["ห้องคลีนิก"];
              lotItems = lotItems.filter(s => !consumableWarehouses.includes(s.warehouse_name));
            }
            const lotTotal = lotItems.reduce((sum, s) => sum + (s.item_amount || 0), 0);
            const st = getStatusConfig(lotTotal, lot.exp_date);
            return {
              color: st.bg,
              label: st.label,
              lotNumber: lot.lot_number || '-',
              expDate: lot.exp_date,
              amount: lotTotal
            };
          })
        : [];

      const images = getImages(firstLot.image_url);
      const thumbUrl = images.full || images.zoom || images.other || images.document || '';

      const galleryImages = [
        { label: '📸 รูปเต็ม', url: images.full, color: '#1abc9c' },
        { label: '🔍 รูปซูม', url: images.zoom, color: '#3498db' },
        { label: '🖼️ รูปอื่นๆ', url: images.other, color: '#9b59b6' },
        { label: '📄 เอกสาร', url: images.document, color: '#f1c40f' }
      ].filter(img => img.url);

      return {
        name,
        lots,
        firstLot,
        isSetGroup,
        groupGlobalMinStock,
        usable,
        totalStock,
        warnings,
        hasExpired,
        hasExpiringSoon,
        isLowStock,
        isExpiringOrExpired,
        lotStatusDots,
        images,
        thumbUrl,
        galleryImages,
        aliases: firstLot.aliases || ''
      };
    });
  }, [itemMaster, supplies, currentPage, getStatusConfig, getImages]);

  // Overall summary metrics across all items in category
  const metrics = useMemo(() => {
    let totalItems = itemGroups.length;
    let totalUsable = 0;
    let lowStockCount = 0;
    let expiringCount = 0;
    let normalCount = 0;

    itemGroups.forEach(item => {
      totalUsable += item.usable;
      if (item.isLowStock) lowStockCount += 1;
      if (item.isExpiringOrExpired) expiringCount += 1;
      if (!item.isLowStock && !item.isExpiringOrExpired) normalCount += 1;
    });

    return { totalItems, totalUsable, lowStockCount, expiringCount, normalCount };
  }, [itemGroups]);

  // Filtered items based on Search Query and Active Status Filter
  const filteredItems = useMemo(() => {
    const query = (inventorySearchQuery || '').trim().toLowerCase();

    return itemGroups.filter(item => {
      // 1. Search Query filter (matches name or aliases)
      if (query) {
        const nameMatches = item.name.toLowerCase().includes(query);
        const aliasMatches = item.aliases && item.aliases.toLowerCase().includes(query);
        if (!nameMatches && !aliasMatches) return false;
      }

      // 2. Active Tab filter
      if (activeFilter === 'low') {
        if (!item.isLowStock) return false;
      } else if (activeFilter === 'expiring') {
        if (!item.isExpiringOrExpired) return false;
      } else if (activeFilter === 'normal') {
        if (item.isLowStock || item.isExpiringOrExpired) return false;
      }

      // 3. Warehouse Filter
      if (activeWarehouse !== 'all') {
        const hasStockInWh = (item.lots || []).some(lot => {
          return (supplies || []).some(s =>
            s && s.master_id === lot.id &&
            s.warehouse_name &&
            s.warehouse_name.includes(activeWarehouse) &&
            (Number(s.item_amount) || 0) > 0
          );
        });
        if (!hasStockInWh) return false;
      }

      return true; // 'all'
    });
  }, [itemGroups, inventorySearchQuery, activeFilter, activeWarehouse, supplies]);

  // Expand / collapse single item lot drawer
  const toggleExpand = (name) => {
    setExpandedItems(prev => ({ ...prev, [name]: !prev[name] }));
  };

  // Toggle 4-slot image gallery drawer
  const toggleGallery = (name) => {
    if (typeof handleTogglePreview === 'function') {
      handleTogglePreview(name);
    }
    setLocalPreview(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const isGalleryOpen = (name) => {
    return !!(showPreview?.[name] ?? localPreview[name]);
  };

  // Toggle expand / collapse all visible items
  const allVisibleExpanded = filteredItems.length > 0 && filteredItems.every(item => expandedItems[item.name]);

  const toggleExpandAll = () => {
    if (allVisibleExpanded) {
      setExpandedItems({});
    } else {
      const newExpanded = {};
      filteredItems.forEach(item => {
        newExpanded[item.name] = true;
      });
      setExpandedItems(newExpanded);
    }
  };

  return (
    <div className="consumable-dashboard-container">
      {/* Header */}
      <div className="consumable-header">
        <h2>
          {currentPage === 'consumable' ? '📦 คลังวัสดุสิ้นเปลือง' : '🏢 คลังวัสดุคงทน & ชุดเครื่องมือ'}
        </h2>
        <p>
          {currentPage === 'consumable'
            ? 'จัดการรายการวัสดุสิ้นเปลือง ตรวจสอบยอดคงเหลือ เกณฑ์ขั้นต่ำ และวันหมดอายุ'
            : 'จัดการรายการวัสดุคงทน ชุดเครื่องมือ และอุปกรณ์ทางการแพทย์'}
        </p>
        <div className="accent-bar" />
        {currentPage === 'consumable' && (
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '14px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="consumable-action-btn primary"
              onClick={() => onNavigate('consumable-dashboard')}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2dd4bf 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                padding: '8px 16px',
                fontSize: '0.9em',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              📊 ดูแดชบอร์ด 4 คลัง
            </button>
            <button
              type="button"
              className="consumable-action-btn transfer"
              onClick={() => onOpenTransfer()}
              style={{
                background: 'linear-gradient(135deg, #00bcd4 0%, #00838f 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                padding: '8px 16px',
                fontSize: '0.9em',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 12px rgba(0, 188, 212, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              🔄 โอนย้ายข้ามคลัง
            </button>
            <button
              type="button"
              className="consumable-action-btn report"
              onClick={() => {
                setReportTargetWarehouse(activeWarehouse !== 'all' ? (activeWarehouse.includes('คลังย่อย') ? 'คลังย่อย (คลังห้องฟัน)' : 'คลังใหญ่ (คลังพัสดุ)') : 'คลังใหญ่ (คลังพัสดุ)');
                setIsReportModalOpen(true);
              }}
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                padding: '8px 16px',
                fontSize: '0.9em',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 12px rgba(13, 148, 136, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              📑 รายงานคลังใหญ่/คลังย่อย
            </button>
            <button
              type="button"
              className="consumable-action-btn report"
              onClick={() => onNavigate && onNavigate('dispense-report')}
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                padding: '8px 16px',
                fontSize: '0.9em',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              📄 รายงานเบิกจ่าย
            </button>
          </div>
        )}
      </div>

      {/* Summary Stats Grid */}
      <div className="consumable-stats-grid">
        {/* Total Items */}
        <div
          className={`consumable-stat-card total ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => setActiveFilter('all')}
          title="คลิกเพื่อดูรายการทั้งหมด"
        >
          <div className="consumable-stat-icon">📦</div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value">{metrics.totalItems}</div>
            <div className="consumable-stat-label">รายการทั้งหมด</div>
          </div>
        </div>

        {/* Total Usable Units */}
        <div
          className={`consumable-stat-card usable ${activeFilter === 'normal' ? 'active' : ''}`}
          onClick={() => setActiveFilter('normal')}
          title="คลิกเพื่อดูรายการสถานะปกติ"
        >
          <div className="consumable-stat-icon">✅</div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value">{metrics.totalUsable.toLocaleString()}</div>
            <div className="consumable-stat-label">ยอดพร้อมใช้รวม</div>
          </div>
        </div>

        {/* Low Stock Warnings */}
        <div
          className={`consumable-stat-card warning ${activeFilter === 'low' ? 'active' : ''}`}
          onClick={() => setActiveFilter('low')}
          title="คลิกเพื่อดูรายการสต็อกต่ำกว่าเกณฑ์"
        >
          <div className="consumable-stat-icon">⚠️</div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value">{metrics.lowStockCount}</div>
            <div className="consumable-stat-label">สต็อกต่ำกว่าเกณฑ์</div>
          </div>
        </div>

        {/* Expiring / Expired */}
        <div
          className={`consumable-stat-card alert ${activeFilter === 'expiring' ? 'active' : ''}`}
          onClick={() => setActiveFilter('expiring')}
          title="คลิกเพื่อดูรายการหมดอายุ/ใกล้หมด"
        >
          <div className="consumable-stat-icon">⏰</div>
          <div className="consumable-stat-content">
            <div className="consumable-stat-value">{metrics.expiringCount}</div>
            <div className="consumable-stat-label">หมดอายุ / ใกล้หมด</div>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="consumable-controls-bar">
        {/* Search input with clear button */}
        <div className="consumable-search-wrapper">
          <span className="consumable-search-icon">🔍</span>
          <input
            type="text"
            className="consumable-search-input"
            placeholder="ค้นหาชื่อวัสดุ หรือชื่ออื่นๆ (Alias)..."
            value={inventorySearchQuery}
            onChange={(e) => setInventorySearchQuery(e.target.value)}
          />
          {inventorySearchQuery && (
            <button
              type="button"
              className="consumable-search-clear"
              onClick={() => setInventorySearchQuery('')}
              title="ล้างคำค้นหา"
            >
              ✕
            </button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="consumable-filter-tabs">
          <button
            type="button"
            className={`consumable-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            ทั้งหมด
            <span className="consumable-filter-count">{metrics.totalItems}</span>
          </button>
          <button
            type="button"
            className={`consumable-filter-btn warning ${activeFilter === 'low' ? 'active' : ''}`}
            onClick={() => setActiveFilter('low')}
          >
            ⚠️ สต็อกต่ำ
            <span className="consumable-filter-count">{metrics.lowStockCount}</span>
          </button>
          <button
            type="button"
            className={`consumable-filter-btn alert ${activeFilter === 'expiring' ? 'active' : ''}`}
            onClick={() => setActiveFilter('expiring')}
          >
            ⏰ หมดอายุ/ใกล้หมด
            <span className="consumable-filter-count">{metrics.expiringCount}</span>
          </button>
          <button
            type="button"
            className={`consumable-filter-btn success ${activeFilter === 'normal' ? 'active' : ''}`}
            onClick={() => setActiveFilter('normal')}
          >
            ✅ ปกติ
            <span className="consumable-filter-count">{metrics.normalCount}</span>
          </button>
        </div>

        {/* Warehouse Filter Tabs */}
        {currentPage === 'consumable' && (
          <div className="consumable-filter-tabs warehouse-tabs" style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.82em', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '600', marginRight: '4px' }}>คลัง:</span>
            {[
              { key: 'all', label: 'ทั้งหมด' },
              { key: 'คลังใหญ่', label: 'คลังใหญ่' },
              { key: 'คลังย่อย', label: 'คลังย่อย' },
              { key: 'ห้องเก็บของ', label: 'ห้องเก็บของ' },
              { key: 'ห้องคลีนิก', label: 'ห้องคลีนิก' }
            ].map((wh) => (
              <button
                key={wh.key}
                type="button"
                className={`consumable-filter-btn ${activeWarehouse === wh.key ? 'active' : ''}`}
                onClick={() => setActiveWarehouse(wh.key)}
                style={{ fontSize: '0.82em', padding: '5px 12px', borderRadius: '20px' }}
              >
                {wh.label}
              </button>
            ))}
          </div>
        )}

        {/* View Mode Toggle */}
        <div className="consumable-view-toggle">
          <button
            type="button"
            className={`consumable-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="แสดงแบบการ์ด"
          >
            🔲 การ์ด
          </button>
          <button
            type="button"
            className={`consumable-view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="แสดงแบบรายการ"
          >
            ☰ รายการ
          </button>
        </div>

        {currentPage === 'consumable' && (
          <div className="consumable-controls-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => onNavigate('consumable-dashboard')}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2dd4bf 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '7px 14px',
                fontSize: '0.85em',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)'
              }}
              title="ดูแดชบอร์ด 4 คลัง"
            >
              📊 ดูแดชบอร์ด 4 คลัง
            </button>
            <button
              type="button"
              onClick={() => onOpenTransfer()}
              style={{
                background: 'linear-gradient(135deg, #00bcd4 0%, #00838f 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '7px 14px',
                fontSize: '0.85em',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                boxShadow: '0 2px 8px rgba(0, 188, 212, 0.25)'
              }}
              title="โอนย้ายข้ามคลัง"
            >
              🔄 โอนย้ายข้ามคลัง
            </button>
            <button
              type="button"
              onClick={() => onNavigate && onNavigate('dispense-report')}
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '7px 14px',
                fontSize: '0.85em',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                boxShadow: '0 2px 8px rgba(5, 150, 105, 0.25)'
              }}
              title="รายงานเบิกจ่าย"
            >
              📄 รายงานเบิกจ่าย
            </button>
            <button
              type="button"
              onClick={() => {
                setReportTargetWarehouse(activeWarehouse !== 'all' ? (activeWarehouse.includes('คลังย่อย') ? 'คลังย่อย (คลังห้องฟัน)' : 'คลังใหญ่ (คลังพัสดุ)') : 'คลังใหญ่ (คลังพัสดุ)');
                setIsReportModalOpen(true);
              }}
              style={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '7px 14px',
                fontSize: '0.85em',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                boxShadow: '0 2px 8px rgba(79, 70, 229, 0.25)'
              }}
              title="สร้างรายงานคลังใหญ่/คลังย่อย"
            >
              📑 รายงานคลังใหญ่/คลังย่อย
            </button>
          </div>
        )}
      </div>

      {/* Sub-bar (Showing results & expand/collapse all) */}
      {filteredItems.length > 0 && (
        <div className="consumable-sub-bar">
          <div>
            แสดง <strong>{filteredItems.length}</strong> รายการ
            {inventorySearchQuery ? ` (จากคำค้นหา "${inventorySearchQuery}")` : ''}
          </div>
          <button
            type="button"
            className="consumable-expand-all-btn"
            onClick={toggleExpandAll}
          >
            {allVisibleExpanded ? '▲ ย่อข้อมูลล็อตทั้งหมด' : '▼ ขยายข้อมูลล็อตทั้งหมด'}
          </button>
        </div>
      )}

      {/* Empty State */}
      {filteredItems.length === 0 && (
        <div className="consumable-empty-state">
          <div className="consumable-empty-icon">🔍</div>
          <div className="consumable-empty-title">ไม่พบรายการวัสดุที่ค้นหา</div>
          <div className="consumable-empty-desc">
            {inventorySearchQuery
              ? `ไม่พบข้อมูลที่ตรงกับคำค้นหา "${inventorySearchQuery}"`
              : 'ไม่มีรายการวัสดุในหมวดหมู่นี้ หรือตามตัวกรองที่เลือก'}
          </div>
          {(inventorySearchQuery || activeFilter !== 'all') && (
            <button
              type="button"
              className="consumable-empty-reset-btn"
              onClick={() => {
                setInventorySearchQuery('');
                setActiveFilter('all');
              }}
            >
              🔄 ล้างการค้นหาและตัวกรอง
            </button>
          )}
        </div>
      )}

      {/* Consumable Cards Grid */}
      <div className={`consumable-grid ${viewMode === 'list' ? 'is-list' : ''}`}>
        {filteredItems.map(item => {
          const isExpanded = Boolean(expandedItems[item.name]);
          const isGalleryActive = isGalleryOpen(item.name);

          return (
            <div
              key={item.name}
              className={`consumable-card${item.isLowStock ? ' has-warning' : ''}${item.hasExpired ? ' has-alert' : ''}${item.isSetGroup ? ' is-set' : ''}`}
            >
              {/* Card Top: Thumbnail + Header Info */}
              <div className="consumable-card-top">
                {/* Image Thumbnail */}
                {item.thumbUrl ? (
                  <div
                    className="consumable-thumb-box"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedImage(item.thumbUrl);
                    }}
                    title="คลิกเพื่อดูรูปขยาย"
                  >
                    <img src={item.thumbUrl} alt={item.name} loading="lazy" />
                  </div>
                ) : (
                  <div className="consumable-thumb-placeholder" title="ไม่มีรูปภาพ">
                    <span className="placeholder-icon">{item.isSetGroup ? '📦' : '🏷️'}</span>
                    <span>ไม่มีรูป</span>
                  </div>
                )}

                {/* Info */}
                <div className="consumable-card-info">
                  <div className="consumable-title-row">
                    <span className="consumable-title">
                      {item.isSetGroup ? '📦 [ชุดตรวจ] ' : ''}{item.name}
                    </span>
                    {item.firstLot?.unit_size && (
                      <span className="consumable-unit-size">
                        {item.firstLot.unit_size}
                      </span>
                    )}
                  </div>

                  {/* Location Badge */}
                  {(item.firstLot?.cabinet || item.firstLot?.shelf || item.firstLot?.row) && (
                    <div className="consumable-badges-row">
                      <span className="consumable-location-badge">
                        📍 {item.firstLot.cabinet ? `ตู้: ${item.firstLot.cabinet}` : ''} {item.firstLot.shelf ? `ชั้น: ${item.firstLot.shelf}` : ''} {item.firstLot.row ? `แถว: ${item.firstLot.row}` : ''}
                      </span>
                    </div>
                  )}

                  {/* Stock Quantity Badge & Dots */}
                  <div className="consumable-badges-row" style={{ marginTop: '2px' }}>
                    <span className={`consumable-stock-badge ${item.usable <= item.groupGlobalMinStock ? 'low' : 'normal'}`}>
                      {item.usable <= item.groupGlobalMinStock ? '⚠️' : '✓'} พร้อมใช้: {item.usable} {item.firstLot?.unit || ''}
                    </span>

                    {/* Expiry / Lot Status Dots */}
                    {currentPage === 'consumable' && item.lotStatusDots.length > 0 && (
                      <div className="consumable-dots-row" title="สถานะตามล็อต">
                        {item.lotStatusDots.map((dot, idx) => (
                          <span
                            key={idx}
                            className="consumable-dot"
                            style={{ backgroundColor: dot.color }}
                            title={`Lot: ${dot.lotNumber} | EXP: ${dot.expDate || '-'} | สถานะ: ${dot.label}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Global Min Stock Row */}
              {currentPage === 'consumable' && !item.isSetGroup && (
                <div className="consumable-min-stock-row">
                  <span>
                    ขั้นต่ำรวม: <strong>{item.groupGlobalMinStock}</strong> {item.firstLot?.unit || ''}
                  </span>
                  <button
                    type="button"
                    className="consumable-min-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpdateGlobalMinStock(e, item.name, item.groupGlobalMinStock);
                    }}
                    title="แก้ไขเกณฑ์ขั้นต่ำรวม"
                  >
                    ✏️ แก้ไขขั้นต่ำรวม
                  </button>
                </div>
              )}

              {/* Warning Chips */}
              {item.warnings.length > 0 && (
                <div className="consumable-warnings-row">
                  {item.warnings.map((w, idx) => (
                    <span key={idx} className="consumable-warning-chip">
                      ⚠️ {w}
                    </span>
                  ))}
                </div>
              )}

              {/* Card Actions */}
              <div className="consumable-card-actions">
                <button
                  type="button"
                  className={`consumable-expand-btn ${isExpanded ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(item.name);
                  }}
                >
                  <span>{isExpanded ? '▲ ซ่อนรายละเอียดล็อต' : '▼ ดูข้อมูลล็อตสต็อก'}</span>
                  <span style={{ opacity: 0.7, fontSize: '0.9em' }}>({item.lots.length} ล็อต)</span>
                </button>

                {currentPage === 'consumable' && item.firstLot && (
                  <>
                    <button
                      type="button"
                      className="consumable-transfer-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenTransfer({
                          masterId: item.firstLot.id,
                          itemName: item.firstLot.item_name,
                          lotNumber: item.firstLot.lot_number
                        });
                      }}
                      style={{
                        background: 'rgba(0, 188, 212, 0.15)',
                        color: '#00e5ff',
                        border: '1px solid rgba(0, 188, 212, 0.35)',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="โอนย้าย"
                    >
                      🔄 โอนย้าย
                    </button>

                    <button
                      type="button"
                      className="consumable-ledger-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const masterLot = item.firstLot || item.lots?.[0];
                        onNavigate && onNavigate('dispense-report', { mode: 'ledger', itemId: masterLot.id });
                      }}
                      style={{
                        background: 'rgba(16, 185, 129, 0.15)',
                        color: '#10b981',
                        border: '1px solid rgba(16, 185, 129, 0.35)',
                        borderRadius: '8px',
                        padding: '6px 12px',
                        fontSize: '0.85em',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="ดูบัญชีวัสดุ"
                    >
                      📑 บัญชีวัสดุ
                    </button>
                  </>
                )}

                {item.galleryImages.length > 0 && (
                  <button
                    type="button"
                    className={`consumable-preview-btn ${isGalleryActive ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGallery(item.name);
                    }}
                    title="ดูรูปภาพทั้งหมด (4 รูป)"
                  >
                    🖼️ {item.galleryImages.length} รูป
                  </button>
                )}
              </div>

              {/* 4-slot Image Gallery Drawer */}
              {isGalleryActive && item.galleryImages.length > 0 && (
                <div className="consumable-gallery-drawer">
                  {item.galleryImages.map((img, i) => (
                    <div key={i} className="consumable-gallery-item">
                      <span className="consumable-gallery-label" style={{ color: img.color }}>{img.label}</span>
                      <div
                        className="consumable-gallery-thumb"
                        style={{ borderColor: img.color }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedImage(img.url);
                        }}
                        title="คลิกเพื่อดูรูปขยาย"
                      >
                        <img src={img.url} alt={img.label} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Accordion / Drawer to view full StockTable */}
              {isExpanded && (
                <div className="consumable-lot-drawer">
                  <StockTable
                    lots={item.lots}
                    supplies={supplies}
                    category={item.firstLot.category}
                    onUpdate={handleUpdateAmount}
                    onDelete={handleDeleteMaster}
                    onEditLot={startEdit}
                    onOpenTransfer={onOpenTransfer}
                    onNavigate={onNavigate}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Warehouse Report Modal */}
      <ConsumableWarehouseReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        itemMaster={itemMaster}
        supplies={supplies}
        logs={[]}
        initialWarehouse={reportTargetWarehouse}
      />
    </div>
  );
};

export default StockPage;
